import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { getTTSAudio } from '../services/ttsService';

const AudioContext = createContext();

export const useAudio = () => useContext(AudioContext);

const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
};

/**
 * Heuristic to detect if text is Marathi/Hindi (Devanagari) or English.
 */
const detectLanguageOfText = (text) => {
    if (!text) return 'en-IN';
    // Match Devanagari range
    const hasDevanagari = /[\u0900-\u097F]/.test(text);
    if (hasDevanagari) {
        // We default to Marathi if Devanagari is found, as it's the primary regional language for mapping
        // The backend TTS engine handle both hi-IN and mr-IN similarly for Devanagari script.
        return 'mr-IN';
    }
    return 'en-IN';
};

export const AudioProvider = ({ children }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [currentText, setCurrentText] = useState('');
    const [fullText, setFullText] = useState('');
    const [progress, setProgress] = useState(0);
    const [source, setSource] = useState(null);
    const [detectedLang, setDetectedLang] = useState('en-IN');
    
    const audioRef = useRef(new Audio());
    const playlistRef = useRef([]);
    const audioCacheRef = useRef(new Map());
    const sequenceIdRef = useRef(0);

    // Sync detectedLang with the UI language (googtrans cookie)
    useEffect(() => {
        const checkLang = () => {
            const cookie = getCookie('googtrans');
            if (cookie) {
                const parts = cookie.split('/');
                const lang = parts[parts.length - 1];
                if (lang === 'mr') setDetectedLang('mr-IN');
                else if (lang === 'hi') setDetectedLang('hi-IN');
                else setDetectedLang('en-IN');
            } else {
                setDetectedLang('en-IN');
            }
        };

        checkLang();
        const interval = setInterval(checkLang, 2000);
        return () => clearInterval(interval);
    }, []);

    const stopAudio = useCallback(() => {
        sequenceIdRef.current += 1;
        audioRef.current.pause();
        audioRef.current.src = '';
        
        // Clean up object URLs to prevent memory leaks
        audioCacheRef.current.forEach(url => URL.revokeObjectURL(url));
        audioCacheRef.current.clear();
        
        setIsPlaying(false);
        setIsLoading(false);
        setProgress(0);
        setCurrentText('');
        setFullText('');
        setSource(null);
    }, []);

    const fetchNextIndices = async (indices, lang, currentId) => {
        for (const idx of indices) {
            if (idx >= playlistRef.current.length) continue;
            if (audioCacheRef.current.has(idx)) continue;
            if (currentId !== sequenceIdRef.current) break;

            try {
                const blob = await getTTSAudio(playlistRef.current[idx], lang, false);
                if (currentId !== sequenceIdRef.current) break;
                const url = URL.createObjectURL(blob);
                audioCacheRef.current.set(idx, url);
            } catch (e) {
                console.error(`Failed to pre-fetch index ${idx}`, e);
            }
        }
    };

    const playIndex = useCallback(async (index, lang, currentId) => {
        if (currentId !== sequenceIdRef.current) return;
        
        if (index >= playlistRef.current.length) {
            stopAudio();
            return;
        }

        const text = playlistRef.current[index];
        setCurrentText(text);

        // Pre-fetch next 2 sentences while this one plays
        fetchNextIndices([index + 1, index + 2], lang, currentId);

        // Wait for this index to be ready if it's not
        const waitForReady = async () => {
             while (!audioCacheRef.current.has(index) && currentId === sequenceIdRef.current) {
                 await new Promise(r => setTimeout(r, 100));
             }
        };
        
        await waitForReady();
        if (currentId !== sequenceIdRef.current) return;

        const url = audioCacheRef.current.get(index);
        audioRef.current.src = url;
        audioRef.current.load();

        audioRef.current.ontimeupdate = () => {
            const d = audioRef.current.duration;
            if (d && !isNaN(d)) {
                const p = (audioRef.current.currentTime / d) * 100;
                setProgress(p);
            }
        };

        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
            playPromise.then(() => {
                if (currentId === sequenceIdRef.current) setIsPlaying(true);
            }).catch(() => {});
        }

        audioRef.current.onended = () => {
            if (currentId === sequenceIdRef.current) playIndex(index + 1, lang, currentId);
        };
    }, [stopAudio]);

    const playNewSequence = useCallback(async (text, lang, fromSource) => {
        stopAudio();
        const currentId = sequenceIdRef.current;
        
        setFullText(text);
        setSource(fromSource);
        setIsLoading(true);

        // 1. Auto detect language from the actual text (prioritize Marathi if any devanagari exists)
        const actualLang = detectLanguageOfText(text);
        
        // 2. Split into sentences for streaming playback
        // We use a regex that handles both Latin (.) and Devanagari (।) punctuation
        playlistRef.current = text.split(/[।.?!]+/).map(s => s.trim()).filter(s => s.length > 1);

        if (playlistRef.current.length === 0) {
            setIsLoading(false);
            return;
        }

        // 3. Start fetching the first sentence immediately
        try {
            const firstBlob = await getTTSAudio(playlistRef.current[0], actualLang, false);
            if (currentId !== sequenceIdRef.current) return;

            const url = URL.createObjectURL(firstBlob);
            audioCacheRef.current.set(0, url);
            setIsLoading(false);

            // 4. Add a small start delay (0.5s) to avoid missing first words and ensure UI is ready
            setTimeout(() => {
                if (currentId === sequenceIdRef.current) {
                    playIndex(0, actualLang, currentId);
                }
            }, 500);

        } catch (error) {
            console.error("Initial TTS fetch failed", error);
            stopAudio();
        }
    }, [stopAudio, playIndex]);

    const togglePlayPause = useCallback(() => {
        if (isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
        } else {
            audioRef.current.play();
            setIsPlaying(true);
        }
    }, [isPlaying]);

    return (
        <AudioContext.Provider value={{
            isPlaying,
            isLoading,
            currentText,
            fullText,
            progress,
            source,
            detectedLang,
            playNewSequence,
            stopAudio,
            togglePlayPause
        }}>
            {children}
        </AudioContext.Provider>
    );
};
